'use client';

import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface RestaurantLeadFormProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function RestaurantLeadForm({ open, onOpenChange }: RestaurantLeadFormProps) {
    const t = useTranslations("leads");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    // Create schema with translated validation messages
    const formSchema = z.object({
        name: z.string().min(2, {
            message: t("nameValidation") || "Name must be at least 2 characters.",
        }),
        restaurantName: z.string().min(2, {
            message: t("restaurantNameValidation") || "Restaurant name must be at least 2 characters.",
        }),
        email: z.string().email({
            message: t("emailValidation") || "Please enter a valid email address.",
        }),
        phone: z.string().min(10, {
            message: t("phoneValidation") || "Please enter a valid phone number.",
        }),
        city: z.string().min(2, {
            message: t("cityValidation") || "City must be at least 2 characters.",
        }),
        message: z.string().optional(),
    });

    type FormValues = z.infer<typeof formSchema>;

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            name: "",
            restaurantName: "",
            email: "",
            phone: "",
            city: "",
            message: "",
        },
    });

    async function onSubmit(data: FormValues) {
        setIsSubmitting(true);

        try {
            // Here you would typically send the data to your API
            console.log("Form data:", data);

            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1000));

            setIsSuccess(true);

            // Reset form after success
            setTimeout(() => {
                form.reset();
                setIsSuccess(false);
                onOpenChange(false);
            }, 2000);

        } catch (error) {
            console.error("Error submitting form:", error);
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="text-xl">{t("title")}</DialogTitle>
                    <DialogDescription>
                        {t("description")}
                    </DialogDescription>
                </DialogHeader>

                {isSuccess ? (
                    <div className="p-6 text-center">
                        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">{t("thankYou")}</h3>
                        <p className="text-gray-600">{t("confirmationMessage")}</p>
                    </div>
                ) : (
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                            <FormField
                                control={form.control}
                                name="name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("yourName")}</FormLabel>
                                        <FormControl>
                                            <Input placeholder={t("namePlaceholder")} {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="restaurantName"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("restaurantName")}</FormLabel>
                                        <FormControl>
                                            <Input placeholder={t("restaurantNamePlaceholder")} {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="email"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>{t("email")}</FormLabel>
                                            <FormControl>
                                                <Input placeholder={t("emailPlaceholder")} type="email" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="phone"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>{t("phone")}</FormLabel>
                                            <FormControl>
                                                <Input placeholder={t("phonePlaceholder")} {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <FormField
                                control={form.control}
                                name="city"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("city")}</FormLabel>
                                        <FormControl>
                                            <Input placeholder={t("cityPlaceholder")} {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="message"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("additionalInfo")}</FormLabel>
                                        <FormControl>
                                            <Textarea
                                                placeholder={t("messagePlaceholder")}
                                                className="resize-none"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <DialogFooter>
                                <Button
                                    type="submit"
                                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            {t("processing")}
                                        </>
                                    ) : (
                                        t("submit")
                                    )}
                                </Button>
                            </DialogFooter>
                        </form>
                    </Form>
                )}
            </DialogContent>
        </Dialog>
    );
} 